import { lazy, Suspense, useEffect } from "react";
import { Plus, RotateCcw, ShieldCheck } from "lucide-react";
import { useReportBuilderStore } from "../store/reportBuilderStore";
import { UploadPage } from "../components/upload/UploadPage";
import { useAppNavigate, useAppRouter } from "./router";

const ReviewPage = lazy(() =>
  import("../components/review/ReviewPage").then((module) => ({
    default: module.ReviewPage,
  })),
);
const SettingsPage = lazy(() =>
  import("../components/settings/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const PreviewPage = lazy(() =>
  import("../components/pdf-preview/PreviewPage").then((module) => ({
    default: module.PreviewPage,
  })),
);

const steps = [
  { path: "/", label: "Upload" },
  { path: "/report/import", label: "Review" },
  { path: "/report/settings", label: "Settings" },
  { path: "/report/preview", label: "Preview" },
];

function AppShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useAppRouter();
  const navigate = useAppNavigate();
  const resetReport = useReportBuilderStore((state) => state.resetReport);
  const hasReport = useReportBuilderStore((state) =>
    Boolean(state.workbookMetadata),
  );
  const activeIndex = Math.max(
    0,
    steps.findIndex((step) => step.path === pathname),
  );

  return (
    <div className="app-shell">
      <header className="app-header">
        <button
          className="brand"
          onClick={() => navigate("/")}
          aria-label="ParkLedger home"
        >
          <span className="brand-mark" aria-hidden="true">
            P
          </span>
          <span>
            <strong>ParkLedger</strong>
            <small>Monthly reporting</small>
          </span>
        </button>

        <nav className="stepper" aria-label="Report workflow">
          {steps.map((step, index) => (
            <button
              key={step.path}
              className={
                index === activeIndex
                  ? "step active"
                  : index < activeIndex
                    ? "step done"
                    : "step"
              }
              onClick={() => {
                if (index === 0 || hasReport) navigate(step.path);
              }}
              disabled={index > 0 && !hasReport}
            >
              <span>{index + 1}</span>
              {step.label}
            </button>
          ))}
        </nav>

        <button
          className="button ghost header-action"
          onClick={() => {
            resetReport();
            navigate("/");
          }}
        >
          {hasReport ? <RotateCcw size={15} /> : <Plus size={15} />}
          New report
        </button>
      </header>
      <main>{children}</main>
      <footer className="site-footer">
        <span>
          <ShieldCheck size={15} /> Files stay in your browser
        </span>
        <span>ParkLedger v1.0</span>
      </footer>
    </div>
  );
}

function Protected({ children }: { children: React.ReactNode }) {
  const hasReport = useReportBuilderStore((state) =>
    Boolean(state.workbookMetadata),
  );
  const navigate = useAppNavigate();
  useEffect(() => {
    if (!hasReport) navigate("/", { replace: true });
  }, [hasReport, navigate]);
  return hasReport ? children : null;
}

function RouteContent() {
  const { pathname } = useAppRouter();
  const navigate = useAppNavigate();
  useEffect(() => {
    if (!steps.some((step) => step.path === pathname)) {
      navigate("/", { replace: true });
    }
  }, [navigate, pathname]);
  if (pathname === "/") return <UploadPage />;
  if (pathname === "/report/import")
    return (
      <Protected>
        <ReviewPage />
      </Protected>
    );
  if (pathname === "/report/settings")
    return (
      <Protected>
        <SettingsPage />
      </Protected>
    );
  if (pathname === "/report/preview")
    return (
      <Protected>
        <PreviewPage />
      </Protected>
    );
  return null;
}

export function App() {
  return (
    <AppShell>
      <Suspense
        fallback={
          <div className="route-loading" role="status">
            Loading report workspace…
          </div>
        }
      >
        <RouteContent />
      </Suspense>
    </AppShell>
  );
}
